'use client';

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@crabs-ai/ds-react';
import { useTransitionDropPreview } from './transition-drop-preview-provider';
import { getCurrentTransitionDragData } from '../editor-property-panel/ui/contents/transition/transition-drag-data';
import TransitionPreview from '../editor-property-panel/ui/contents/transition/TransitionPreview';

interface DragState {
  cursorPosition: { x: number; y: number };
}

// 드래그 오버레이 크기 (원본 TransitionItem보다 작게)
const OVERLAY_WIDTH = 120;
const OVERLAY_HEIGHT = 87.5;

/**
 * 트랜지션 드래그 시 마우스를 따라다니는 커스텀 오버레이
 * 타겟(경계)이 정해지면 숨겨지고, 타겟이 없으면 표시됨
 * TransitionItem과 동일한 디자인 (애니메이션 없이)
 */
export const TransitionDragOverlay: React.FC = () => {
  const { previewState } = useTransitionDropPreview();
  const [dragState, setDragState] = useState<DragState | null>(null);

  // 전역 드래그 이벤트 리스닝
  useEffect(() => {
    const handleDragOver = (e: DragEvent) => {
      const transitionData = getCurrentTransitionDragData();
      if (transitionData) {
        setDragState({
          cursorPosition: { x: e.clientX, y: e.clientY },
        });
      }
    };

    const handleDragEnd = () => {
      setDragState(null);
    };

    document.addEventListener('dragover', handleDragOver);
    document.addEventListener('dragend', handleDragEnd);
    document.addEventListener('drop', handleDragEnd);

    return () => {
      document.removeEventListener('dragover', handleDragOver);
      document.removeEventListener('dragend', handleDragEnd);
      document.removeEventListener('drop', handleDragEnd);
    };
  }, []);

  // 타겟이 정해지면 (previewState가 있으면) 숨김
  // 또는 dragState가 없으면 숨김
  if (!dragState || previewState) {
    return null;
  }

  const { cursorPosition } = dragState;

  // 드래그 중인 트랜지션 데이터 가져오기
  const transitionData = getCurrentTransitionDragData();
  if (!transitionData) {
    return null;
  }

  const { type, name } = transitionData;

  return createPortal(
    <div
      className="flex flex-col items-start rounded-2xs bg-sys-bg-surface-primary p-5xs opacity-30"
      style={{
        position: 'fixed',
        top: cursorPosition.y - OVERLAY_HEIGHT / 2,
        left: cursorPosition.x - OVERLAY_WIDTH / 2,
        width: OVERLAY_WIDTH,
        pointerEvents: 'none',
        zIndex: 9999,
        opacity: 0.9,
      }}
    >
      {/* 트랜지션 프리뷰 - TransitionItem과 동일한 구조 */}
      <div className={cn('aspect-[16/9] w-full overflow-hidden rounded-xs')}>
        <TransitionPreview
          type={type}
          progress={0}
        />
      </div>
      {/* 트랜지션 이름 */}
      <span className="p-4xs text-center text-caption-xsmall text-sys-cont-neutral-default">
        {name}
      </span>
    </div>,
    document.body,
  );
};
